import React, { useState, useEffect } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
  GridRenderCellParams,
  GridToolbarContainer
} from '@mui/x-data-grid';
import {
  Chip,
  Link,
  TextField,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button as MaterialButton,
  CircularProgress
} from "@mui/material";
import IPage from "../../../types/page.type";
import {Box, Breadcrumbs, Grid, Typography, Button} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ISale from "../../../types/sale.type";
import {getSales, deleteById, exportSales} from "../../../services/sale.service";
import moment from "moment/moment";
import {SaleStatus} from "../../../types/enumeration/SaleStatus";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmationModal from "../../utils/ConfirmationModal";
import showNotification from "../../utils/Notify";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import {SaleStatusNames} from "../../../types/enumeration/SaleStatusNames";
import {SaleStatusColors} from "../../../types/enumeration/SaleStatusColors";

const SalesList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [salesPage, setSalesPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [keywordFilter, setKeywordFilter] = useState( "");
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [selectedSaleId, setSelectedSaleId] = React.useState<any>();
  const [statusFilter, setStatusFilter] = useState( "-1");

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<ISale>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1
    },
    {
      field: 'invoiceNumber',
      headerName: 'Nro. Comprobante',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'saleType',
      headerName: 'Tipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${params.row.saleType ? params.row.saleType.name : '-' }`}</span>;
      }
    },
    {
      field: 'presentationDate',
      headerName: 'Fecha de Presentacion',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${moment(params.row.presentationDate).format('DD/MM/YYYY')}`}</span>;
      }
    },
    {
      field: 'effectiveCollectionDate',
      headerName: 'Fecha Efectiva de Cobro',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${moment(params.row.presentationDate).format('DD/MM/YYYY')}`}</span>;
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={SaleStatusColors[SaleStatus[params.row.status as keyof typeof SaleStatus]]}
                     label={SaleStatusNames[SaleStatus[params.row.status as keyof typeof SaleStatus]]}
                     sx={{width: 160, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      cellClassName: 'actions',
      getActions: (params) => {
        const { id, status } = params.row;
        const actions = [
          <GridActionsCellItem
            icon={ status === SaleStatus.CASHED ? (<RemoveRedEyeIcon />) : (<EditIcon />) }
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
        if (status !== SaleStatus.CASHED) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteForeverIcon />}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />
          );
        }
        return actions;
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id)
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setSelectedSaleId(id);
    setConfirmationModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setSelectedSaleId(undefined);
    setConfirmationModalOpen(false);
  }

  const onConfirm = () => {
    deleteById(selectedSaleId)
      .then(() => {
        showNotification({type: 'success', message: 'Venda eliminada correctamente.'});
        handleConfirmationModalClose();
        fetchSales();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setKeywordFilter(event.target.value)
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchSales(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchSales(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const buildPurchaseParams = (
    page?: number,
    size?: number,
    sort: string = sortColumn,
    order: any = sortDirection
  ) => {
    type Param = {[key: string] : string}
    const params: Param = {}

    if (page !== undefined) {
      params.page = String(page);
    }

    if (size !== undefined) {
      params.size = String(size);
    }

    params.sort = sort;
    params.order = order;

    if (keywordFilter) {
      params.keyword = keywordFilter;
    }

    if (statusFilter !== '-1') {
      params.status = statusFilter;
    }

    return params;
  };

  const fetchSales = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);

    const params = buildPurchaseParams(page, size, sort, order);

    getSales(params).then( (response) => {
      setSalesPage(response.data)
      setLoading(false);
    })
  };

  const handleExportPurchases = async () => {
    try {
      setExporting(true);

      const params = buildPurchaseParams(undefined, undefined, sortColumn, sortDirection);

      await exportSales(params);
    } finally {
      setExporting(false);
    }
  };

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <MaterialButton
          variant="outlined"
          color="success"
          endIcon={
            exporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExportPurchases}
        >
          Exportar Excel
        </MaterialButton>
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    // fetchRoles()
    fetchSales();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <PointOfSaleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Control Financiero
              </Typography>
              <Typography level="h4" component="h1">
                Ventas
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nuevo
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>

          <ConfirmationModal
            open={confirmationModalOpen}
            onConfirm={onConfirm}
            onCancel={handleConfirmationModalClose} />

          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={6} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="keyword"
                  label="Buscar por Nombre de Usuario y/o Email"
                  name="keyword"
                  variant="filled"
                  type="text"
                  onChange={handleChangeKeyword}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchSales();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={3} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="type-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    label="type"
                    value={statusFilter}
                    onChange={handleChangeStatus}
                  >
                    <MenuItem key="-1" value="-1">Todos</MenuItem>
                    {Object
                      .keys(SaleStatus)
                      .map((key) => (
                        <MenuItem key={key} value={SaleStatus[key as keyof typeof SaleStatus]}>
                          {SaleStatusNames[SaleStatus[key as keyof typeof SaleStatus]]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>
            <Grid xl={3} md={6} sm={6} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchSales()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 680, typography: 'body1' }} ml={1} mt={0} pr={2}>
            <CssVarsProvider>
              <DataGrid
                slots={{toolbar: EditToolbar}}
                rows={salesPage.content}
                columns={columns}
                columnVisibilityModel={{
                  id: false,
                }}
                paginationModel={paginationModel}
                rowCount={salesPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SalesList;
